import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Form, Input, Button, Checkbox, Card, message, Row, Col } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { VersionInfo } from '../../utils/version';
import CaptchaInput from '../../components/auth/CaptchaInput';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';

// 记住我功能的localStorage key
const REMEMBER_EMAIL_KEY = 'greenbond_remember_email';
const REMEMBER_ME_KEY = 'greenbond_remember_me';
const LOGIN_ATTEMPTS_KEY = 'greenbond_login_attempts';

// 登录失败多少次后显示验证码
const CAPTCHA_THRESHOLD = 3;

interface LoginFormData {
  email: string;
  password: string;
  remember?: boolean;
  captchaCode?: string;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loginError, setLoginError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const { login, isLoading, error, clearAuthError } = useAuth();

  // 验证码相关状态
  const [loginAttempts, setLoginAttempts] = useState<number>(() => {
    const saved = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [captchaId, setCaptchaId] = useState<string>('');
  const [showCaptcha, setShowCaptcha] = useState<boolean>(() => {
    const saved = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    return saved ? parseInt(saved, 10) >= CAPTCHA_THRESHOLD : false;
  });
  const [captchaRefreshTrigger, setCaptchaRefreshTrigger] = useState(0);

  // 页面加载时检查是否有保存的邮箱或来自注册页面的邮箱
  useEffect(() => {
    const state = location.state as { message?: string; email?: string };

    // 如果有来自注册页面的消息，显示它
    if (state?.message) {
      setSuccessMessage(state.message);
      // 清除 state 以避免刷新时重复显示
      window.history.replaceState({}, document.title);
    }

    // 优先使用来自注册页面的邮箱，否则使用保存的邮箱
    if (state?.email) {
      form.setFieldsValue({
        email: state.email,
      });
    } else {
      const savedRemember = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
      const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);

      if (savedRemember && savedEmail) {
        form.setFieldsValue({
          email: savedEmail,
          remember: true,
        });
      }
    }
  }, [form, location]);

  const handleSubmit = async (values: LoginFormData) => {
    clearAuthError();
    setLoginError(''); // 清除之前的错误信息

    // 如果需要显示验证码但未填写
    if (showCaptcha) {
      if (!captchaId || !values.captchaCode) {
        setLoginError(t('auth:login.captcha.required'));
        return;
      }

      // 验证验证码
      try {
        const captchaResponse = await fetch('/api/v1/auth/captcha/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            captchaId,
            captchaCode: values.captchaCode,
          }),
        });
        const captchaResult = await captchaResponse.json();

        if (!captchaResult.success) {
          setLoginError(
            captchaResult.error?.message || t('auth:login.captcha.invalid')
          );
          // 刷新验证码
          form.setFieldsValue({ captchaCode: '' });
          setCaptchaRefreshTrigger(prev => prev + 1);
          return;
        }
      } catch {
        setLoginError(t('auth:login.captcha.invalid'));
        setCaptchaRefreshTrigger(prev => prev + 1);
        return;
      }
    }

    try {
      const result = await login({
        email: values.email,
        password: values.password,
        rememberMe: values.remember,
      });

      if (result.success) {
        // 登录成功，清除失败计数
        localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
        setLoginAttempts(0);
        setShowCaptcha(false);

        // 处理"记住我"功能
        if (values.remember) {
          // 保存邮箱到localStorage
          localStorage.setItem(REMEMBER_EMAIL_KEY, values.email);
          localStorage.setItem(REMEMBER_ME_KEY, 'true');
        } else {
          // 清除保存的邮箱
          localStorage.removeItem(REMEMBER_EMAIL_KEY);
          localStorage.removeItem(REMEMBER_ME_KEY);
        }

        // 根据用户角色导航到不同页面
        const userRole = result.data?.user?.role;
        switch (userRole) {
          case 'admin':
            navigate('/admin/user-management');
            break;
          case 'issuer':
            // Bugfix: 发行人首次登录默认进入"我的问卷"
            navigate('/admin/issuer/questionnaires');
            break;
          case 'exchange':
            navigate('/admin/screening');
            break;
          default:
            navigate('/admin/screening');
        }
      } else {
        // 登录失败，增加计数器
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        localStorage.setItem(LOGIN_ATTEMPTS_KEY, newAttempts.toString());

        // 如果达到阈值，显示验证码
        if (newAttempts >= CAPTCHA_THRESHOLD) {
          setShowCaptcha(true);
        }

        // 如果已经在显示验证码，需要刷新（因为验证码是一次性的）
        if (showCaptcha) {
          form.setFieldsValue({ captchaCode: '' });
          setCaptchaRefreshTrigger(prev => prev + 1);
        }

        // 在页面上显示具体的登录错误信息
        setLoginError(result.error || t('auth:login.invalidCredentials'));
      }
    } catch (error) {
      // 处理网络错误等异常情况
      console.error('Login error:', error);
      setLoginError(t('common:message.networkError'));
    }
  };

  return (
    <Row className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* 左侧品牌展示区域 - Ant Design Grid响应式 */}
      <Col
        xs={0} // <576px 隐藏
        sm={0} // ≥576px 隐藏
        md={0} // ≥768px 隐藏
        lg={14} // ≥992px 显示，占14/24 = 58.3%
        xl={16} // ≥1200px 显示，占16/24 = 66.7%
        className="hidden lg:flex"
        style={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* 主背景渐变 - 更加柔和优雅 */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600"></div>

        {/* 次级渐变层 - 增加深度 */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-white/5"></div>

        {/* 精致的装饰性几何图形 */}
        <div className="absolute inset-0">
          {/* 主要装饰圆 */}
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-white/15 rounded-full blur-3xl"></div>

          {/* 次要装饰元素 */}
          <div className="absolute top-1/2 right-1/2 w-32 h-32 bg-white/20 rounded-full blur-2xl"></div>
          <div className="absolute top-3/4 right-1/3 w-24 h-24 bg-white/25 rounded-full blur-xl"></div>

          {/* 网格纹理 */}
          <div className="absolute inset-0 opacity-5">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
        </div>

        {/* 主要内容 - 相对定位，由父容器居中 */}
        <div
          className="relative z-10 text-white max-w-2xl"
          style={{
            padding: '0 4rem',
            width: '100%',
          }}
        >
          {/* 品牌标识 - 更加精致 */}
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-white/25 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-5 border border-white/40 shadow-lg">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <span className="text-2xl">🌱</span>
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight drop-shadow-sm">
                  {t('auth:brand.name')}
                </h1>
                <p className="text-white/85 text-base font-light">
                  {t('auth:brand.slogan')}
                </p>
              </div>
            </div>
          </div>

          {/* 主标题 - 更有冲击力 */}
          <div className="mb-16">
            <div className="space-y-4">
              <h2 className="text-3xl xl:text-4xl font-bold leading-tight tracking-tight drop-shadow-sm">
                {t('auth:brand.heroTitle')}
                <br />
                <span className="bg-gradient-to-r from-white via-emerald-100 to-emerald-200 bg-clip-text text-transparent drop-shadow-none">
                  {t('auth:brand.heroHighlight')}
                </span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-white via-white/80 to-white/50 rounded-full shadow-sm"></div>
            </div>
            <p className="text-lg text-white/90 leading-relaxed mt-8 max-w-lg">
              {t('auth:brand.heroDescription')}
            </p>
          </div>

          {/* 核心价值 - 简化展示 */}
          <div className="space-y-6">
            <div className="flex items-center space-x-4 group cursor-pointer">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30 group-hover:bg-white/35 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <svg
                  className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1 group-hover:text-emerald-100 transition-colors duration-300">
                  {t('auth:brand.feature1Title')}
                </h3>
                <p className="text-white/80 group-hover:text-white/95 transition-colors duration-300">
                  {t('auth:brand.feature1Desc')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 group cursor-pointer">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30 group-hover:bg-white/35 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <svg
                  className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1 group-hover:text-emerald-100 transition-colors duration-300">
                  {t('auth:brand.feature2Title')}
                </h3>
                <p className="text-white/80 group-hover:text-white/95 transition-colors duration-300">
                  {t('auth:brand.feature2Desc')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 group cursor-pointer">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30 group-hover:bg-white/35 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <svg
                  className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1 group-hover:text-emerald-100 transition-colors duration-300">
                  {t('auth:brand.feature3Title')}
                </h3>
                <p className="text-white/80 group-hover:text-white/95 transition-colors duration-300">
                  {t('auth:brand.feature3Desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Col>

      {/* 右侧登录表单区域 - Ant Design Grid响应式 */}
      <Col
        xs={24} // <576px 全宽
        sm={24} // ≥576px 全宽
        md={24} // ≥768px 全宽
        lg={10} // ≥992px 占10/24 = 41.7%
        xl={8} // ≥1200px 占8/24 = 33.3%
        className="bg-white/60 backdrop-blur-md lg:flex lg:items-center lg:justify-center"
        style={{
          padding: '1rem 1rem',
          minHeight: '100vh',
          position: 'relative',
        }}
      >
        {/* 桌面端语言切换按钮 - 绝对定位右上角 */}
        <div
          className="hidden lg:block"
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1.5rem',
            zIndex: 10,
          }}
        >
          <LanguageSwitcher
            size="middle"
            showLabel={true}
            buttonStyle={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            }}
          />
        </div>

        <div className="w-full max-w-md">
          {/* 移动端顶部区域：Logo + 语言切换 在同一行 */}
          <div className="lg:hidden mb-6">
            <div className="flex items-center justify-between">
              {/* 左侧：品牌 Logo */}
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center mr-2.5 shadow-md">
                  <span className="text-lg">🌱</span>
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 tracking-tight leading-tight">
                    {t('auth:brand.name')}
                  </h1>
                  <p className="text-[10px] text-gray-400 font-medium">
                    {t('auth:brand.slogan')}
                  </p>
                </div>
              </div>
              {/* 右侧：语言切换 - 小巧精致 */}
              <LanguageSwitcher
                size="small"
                showLabel={false}
                buttonStyle={{
                  background: 'transparent',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  color: '#6b7280',
                  padding: '4px 8px',
                  height: '28px',
                  fontSize: '12px',
                }}
              />
            </div>
          </div>

          {/* 登录卡片 - 全新设计 */}
          <div className="bg-white/98 backdrop-blur-xl rounded-2xl shadow-2xl border border-emerald-300/40 overflow-hidden ring-1 ring-emerald-200/20">
            {/* 卡片头部 - 翡翠绿方案 */}
            <div className="px-6 pt-5 pb-4 bg-gradient-to-r from-emerald-500 to-teal-500">
              <div className="text-center">
                <h2 className="text-xl font-bold text-white mb-1 tracking-tight">
                  {t('auth:login.subtitle')}
                </h2>
                <p className="text-white/90 text-sm font-medium">
                  {t('auth:login.title')}
                </p>
              </div>
            </div>

            {/* 表单区域 */}
            <div className="px-6 py-5 bg-gradient-to-b from-white to-gray-50/30">
              {/* 成功消息显示 */}
              {successMessage && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="text-emerald-600 text-sm font-medium">
                      {successMessage}
                    </div>
                  </div>
                </div>
              )}

              {/* 错误信息显示 */}
              {loginError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="text-red-600 text-sm font-medium">
                      {loginError}
                    </div>
                  </div>
                </div>
              )}

              <Form
                form={form}
                onFinish={handleSubmit}
                layout="vertical"
                size="large"
                requiredMark={false}
              >
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: t('auth:login.email.required') },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                    placeholder={t('auth:login.email.placeholder')}
                    style={{
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '16px',
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t('auth:login.password.required'),
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                    placeholder={t('auth:login.password.placeholder')}
                    iconRender={visible =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    style={{
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '16px',
                    }}
                  />
                </Form.Item>

                {/* 验证码 - 登录失败3次后显示 */}
                {showCaptcha && (
                  <Form.Item
                    name="captchaCode"
                    rules={[
                      {
                        required: true,
                        message: t('auth:login.captcha.required'),
                      },
                    ]}
                  >
                    <CaptchaInput
                      onCaptchaIdChange={setCaptchaId}
                      placeholder={t('auth:login.captcha.placeholder')}
                      refreshTrigger={captchaRefreshTrigger}
                    />
                  </Form.Item>
                )}

                <div className="flex items-center justify-between mb-4">
                  <Form.Item
                    name="remember"
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Checkbox>{t('auth:login.rememberMe')}</Checkbox>
                  </Form.Item>
                  <Link
                    to="/auth/forgot-password"
                    className="text-emerald-600 hover:text-emerald-700 text-sm"
                  >
                    {t('auth:login.forgotPassword')}
                  </Link>
                </div>

                <Form.Item style={{ margin: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    block
                    style={{
                      background:
                        'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      height: '44px',
                      fontSize: '15px',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    }}
                  >
                    {isLoading
                      ? t('auth:login.submitting')
                      : t('auth:login.submit')}
                  </Button>
                </Form.Item>
              </Form>

              {/* 错误信息显示 */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* 注册链接已隐藏 */}
            </div>
          </div>

          {/* 问卷入口卡片 - 紧凑设计 */}
          <div className="mt-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 text-white shadow-lg">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-lg">🎯</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold">
                  {t('auth:quickAssess.title')}
                </h3>
                <p className="text-emerald-100 text-xs">
                  {t('auth:quickAssess.subtitle')}
                </p>
              </div>
              <Link to="/questionnaire" className="flex-shrink-0 ml-3">
                <button className="bg-white text-emerald-600 font-semibold py-2 px-4 rounded-lg text-sm hover:bg-emerald-50 transition-all duration-200 shadow">
                  {t('auth:quickAssess.button')}
                </button>
              </Link>
            </div>
          </div>

          {/* 底部版权和版本信息 */}
          <div className="text-center mt-4 space-y-2">
            {/* 版本号显示 */}
            <div className="flex items-center justify-center space-x-2">
              <div className="flex items-center space-x-1.5 bg-gray-100 px-2 py-1 rounded">
                <div className="w-4 h-4 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">GB</span>
                </div>
                <span className="text-xs font-semibold text-emerald-600">
                  {VersionInfo.displayVersion}
                </span>
              </div>
              <div
                className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={{
                  backgroundColor: VersionInfo.statusColors.bg,
                  color: VersionInfo.statusColors.text,
                  border: `1px solid ${VersionInfo.statusColors.border}`,
                }}
              >
                {VersionInfo.status}
              </div>
            </div>

            {/* 版权信息 */}
            <p className="text-gray-400 text-xs">
              {t('auth:copyright', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default LoginPage;
